import { computed, ref, type Ref } from 'vue'
import type { JobLookupFilterValue, MultiselectOption } from '@ironsight/types'
import { isSelectAllValue } from './useFilterChips'

export interface SelectAllFilterField {
  supportsSelectAll: boolean
  filteredOptions: MultiselectOption[]
  selectAllFiltered: () => void
  deselectAllFiltered: () => void
  areAllFilteredSelected?: () => boolean
}

function extractValueId(item: unknown): string | number | null {
  return typeof item === 'object' && item !== null && 'value' in item
    ? (item as MultiselectOption).value
    : (item as string | number | null)
}

export function useSelectAllToggle(value: Ref<JobLookupFilterValue | null>) {
  const filterFieldRef = ref<SelectAllFilterField | null>(null)

  const isMultiselect = computed(() => filterFieldRef.value?.supportsSelectAll === true)

  const childFilteredOptions = computed(() => filterFieldRef.value?.filteredOptions ?? [])

  const allFilteredSelected = computed(() => {
    if (filterFieldRef.value?.areAllFilteredSelected) {
      return filterFieldRef.value.areAllFilteredSelected()
    }

    if (isSelectAllValue(value.value)) return true

    const filtered = childFilteredOptions.value
    if (filtered.length === 0) return false
    if (!value.value || value.value.length === 0) return false

    const selectedValues = new Set(value.value.map(extractValueId))
    return filtered.every((opt) => selectedValues.has(opt.value))
  })

  function handleToggleAll() {
    if (allFilteredSelected.value) {
      filterFieldRef.value?.deselectAllFiltered?.()
    } else {
      filterFieldRef.value?.selectAllFiltered?.()
    }
  }

  return { filterFieldRef, isMultiselect, allFilteredSelected, handleToggleAll }
}
